import { useQuery } from '@tanstack/react-query'
import type { UseQueryOptions, QueryKey } from '@tanstack/react-query'
import { usePermissions } from './usePermissions'

export interface UsePermittedQueryOptions<
  TData,
  TError = Error,
  TKey extends QueryKey = QueryKey,
> extends Omit<UseQueryOptions<TData, TError, TData, TKey>, 'enabled'> {
  /** The permission module required (from GET /users/me roleId.permissions). */
  module: string
  /** The permission action required. */
  action: string
  /**
   * Additional caller-controlled enabled flag (ANDed with the permission check).
   * Defaults to true — the query still won't run until the permission is confirmed.
   */
  enabled?: boolean
}

/**
 * Wrapper around useQuery that refuses to fire until:
 *   1. GET /users/me permissions are resolved (not loading), AND
 *   2. The user has the required module+action permission, AND
 *   3. The caller's own `enabled` flag is true (default: true).
 *
 * This is the single source of truth for permission-gated queries.
 * Use it for every hook that fetches privileged data so no caller can
 * accidentally bypass the check by omitting an `enabled` option.
 */
export function usePermittedQuery<
  TData,
  TError = Error,
  TKey extends QueryKey = QueryKey,
>({
  module,
  action,
  enabled = true,
  ...queryOptions
}: UsePermittedQueryOptions<TData, TError, TKey>) {
  const { can, isLoading: permissionsLoading } = usePermissions()

  return useQuery<TData, TError, TData, TKey>({
    ...(queryOptions as UseQueryOptions<TData, TError, TData, TKey>),
    enabled: !permissionsLoading && can(module, action) && enabled,
  })
}
